// Feature P8: yards change between visits. The save keeps a compact visit
// list per layout key (built-ins, Home turf venues and real "Your street"
// yards alike); when the board offers a yard the player has worked before,
// 1–2 mutations that weren't there last time are added on top of the usual
// draw (contract.ts `withRevisit`), and the card says what's new.

import type { Contract, MutationKey } from './types';
import type { SaveGame } from './economy';

/** One yard's visits: layout key, how many times, and the mutations it had last time. */
export interface VisitRecord {
  k: string;
  n: number;
  m: MutationKey[];
}

/** Most-recent yards kept (oldest dropped first). */
export const VISITS_KEEP = 60;

/** Human names for mutation keys, for "New since last time: …". */
export const MUTATION_NAMES: Record<MutationKey, string> = {
  'veggie-bed': 'veggie bed',
  'above-ground-pool': 'above-ground pool',
  trampoline: 'trampoline',
  'bin-night': 'bins out',
  'magpie-season': 'nesting magpie',
  sprinkler: 'sprinkler',
  bindii: 'bindii',
  'heavy-leaves': 'leaf drop',
  ibis: 'bin chickens',
  'lost-ball': 'lost ball',
  'hot-day': 'heatwave',
};

const KEYS = new Set(Object.keys(MUTATION_NAMES));

export function visitsOf(save: Pick<SaveGame, 'visits'>): VisitRecord[] {
  return Array.isArray(save.visits) ? save.visits : [];
}

export function visitOf(save: Pick<SaveGame, 'visits'>, layoutKey: string): VisitRecord | undefined {
  return visitsOf(save).find((v) => v.k === layoutKey);
}

/** A visit to a yard: bump its count, remember this visit's mutations, most recent last. Pure. */
export function recordVisit(save: SaveGame, layoutKey: string, mutations: readonly MutationKey[]): SaveGame {
  const prev = visitOf(save, layoutKey);
  const rest = visitsOf(save).filter((v) => v.k !== layoutKey);
  const visits = [...rest, { k: layoutKey, n: (prev?.n ?? 0) + 1, m: mutations.slice() }].slice(-VISITS_KEEP);
  return { ...save, visits };
}

/** Tolerant parse of a stored visit list (drops junk, unknown keys and duplicates; keeps the last of each). */
export function cleanVisits(x: unknown): VisitRecord[] {
  if (!Array.isArray(x)) return [];
  const out = new Map<string, VisitRecord>();
  for (const v of x) {
    const r = v as Partial<VisitRecord>;
    if (!r || typeof r.k !== 'string' || !r.k) continue;
    const n = Number.isFinite(r.n) ? Math.max(1, Math.floor(r.n as number)) : 1;
    const m = Array.isArray(r.m) ? r.m.filter((k): k is MutationKey => KEYS.has(k as string)) : [];
    out.delete(r.k);
    out.set(r.k, { k: r.k, n, m });
  }
  return [...out.values()].slice(-VISITS_KEEP);
}

/** "New since last time: trampoline" (or "…: trampoline and bins out"); '' when nothing's new. */
export function newSinceLine(c: Pick<Contract, 'newSince'>): string {
  const names = (c.newSince ?? []).map((k) => MUTATION_NAMES[k] ?? k);
  if (!names.length) return '';
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `New since last time: ${list}`;
}
